/**
 * Tiny scoped logger.
 *
 * Why: most of the codebase uses `console.log` / `console.warn` directly, unconditional.
 * That's noisy in production (every operator session spams the dev console with socket
 * lifecycle + scroll trace + dedup decisions) and gives no way to silence one subsystem
 * without losing all of them.
 *
 * Usage:
 *   const log = createLogger('Socket');
 *   log.debug('connected', { id });    // off by default in prod; on in dev
 *   log.info('something noteworthy');  // always on
 *   log.warn('recoverable');           // always on
 *   log.error('bad', err);             // always on
 *
 * Debug output:
 *   - Always emitted in `import.meta.env.DEV`.
 *   - In prod, gated by `VITE_DEBUG_LOG`:
 *       VITE_DEBUG_LOG="*"           → all scopes
 *       VITE_DEBUG_LOG="Socket,Auth" → comma-separated scope list
 *       VITE_DEBUG_LOG=""/unset      → off
 *
 * Output shape: `[Scope] message …rest` — matches the existing prefix convention so
 * the migration from `console.log('[Socket] …', …)` is a literal swap.
 */

type LogArg = unknown;

const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
const isDev = !!env.DEV;
const debugScopesRaw = (env.VITE_DEBUG_LOG ?? '').trim();

const debugAll = debugScopesRaw === '*';
const debugScopes = new Set(
  debugScopesRaw && !debugAll
    ? debugScopesRaw.split(',').map((s) => s.trim()).filter(Boolean)
    : [],
);

function debugEnabled(scope: string): boolean {
  if (isDev) return true;
  if (debugAll) return true;
  return debugScopes.has(scope);
}

export interface ScopedLogger {
  debug(...args: LogArg[]): void;
  info(...args: LogArg[]): void;
  warn(...args: LogArg[]): void;
  error(...args: LogArg[]): void;
}

export function createLogger(scope: string): ScopedLogger {
  const prefix = `[${scope}]`;
  return {
    debug(...args) {
      if (!debugEnabled(scope)) return;
      console.log(prefix, ...args);
    },
    info(...args) {
      console.log(prefix, ...args);
    },
    warn(...args) {
      console.warn(prefix, ...args);
    },
    error(...args) {
      console.error(prefix, ...args);
    },
  };
}
